import { hp, isMobile, wp } from '@shared/utils/dimensions';
import {RFPercentage, RFValue} from '@shared/utils/fontscale';
import {PixelRatio, Platform} from 'react-native';

function getFontSize(base: number) {
  const fontScale = PixelRatio.getFontScale();
  if (fontScale > 1.5) {
    return RFPercentage(0.4);
  }
  return RFValue(base);
}

/**
 * Typography constants for consistent text, spacing, and UI dimensions across the app.
 * fontSize
 * fontWeight
 * spacing
 * radius
 * borderWidth
 * icon
 * buttonHeight
 * lineHeight
 * margin
 * padding
 * gap
 * textInput
 * fontFamily
 * fullWidth
 * fullHeight
 * signatureDimensions
 * height
 * textAlign
 * contentAlign
 * position
 */
const Typography = {
  /**
   * Standard font sizes (in points)
   * Usage: Typography.fontSize.md
   */
  fontSize: {
    fontSize10: getFontSize(5), // Result: 10px
    fontSize11: getFontSize(5.5), // Result: 11px
    fontSize12: getFontSize(6), // Result: 12px
    fontSize13: getFontSize(6.5), // Result: 13px
    fontSize14: getFontSize(7), // Result: 14px
    fontSize15: getFontSize(7.5), // Result: 15px
    fontSize16: getFontSize(8), // Result: 16px
    fontSize18: getFontSize(9), // Result: 18px
    fontSize20: getFontSize(10), // Result: 20px
    fontSize22: getFontSize(11), // Result: 22px
    fontSize24: getFontSize(12), // Result: 24px
    fontSize26: getFontSize(13), // Result: 26px
    fontSize28: getFontSize(14), // Result: 28px
    fontSize30: getFontSize(15), // Result: 30px
    fontSize32: getFontSize(16), // Result: 32px
    fontSize34: getFontSize(17), // Result: 34px
    fontSize36: getFontSize(18), // Result: 36px
    fontSize38: getFontSize(19), // Result: 38px
    fontSize40: getFontSize(20), // Result: 40px
    fontSize42: getFontSize(21), // Result: 42px
    fontSize44: getFontSize(22), // Result: 44px
    fontSize46: getFontSize(23), // Result: 46px
    fontSize48: getFontSize(24), // Result: 48px
    fontSize50: getFontSize(25), // Result: 50px
  } as const,
  /**
   * Standard font weights
   * Usage: Typography.fontWeight.medium
   */
  fontWeight: {
    regular: '400',
    medium: '500',
    semiBold: '600',
    bold: '700',
    fullBold: '800'
  } as const,
  /**
   * Standard spacing values (in pixels) for margins, paddings, etc.
   * Usage: Typography.spacing.md
   */
  spacing: {
    spacing4: 4,
    spacing8: 8,
    spacing16: 16,
    spacing24: 24,
    spacing7: 7,
  } as const,
  /**
   * Border radius values for rounded corners
   * Usage: Typography.radius.md
   */
  radius: {
    radius015: 1.5,
    radius2: 2,
    radius4: 4,
    radius6: 6,
    radius8: 8,
    radius9: 9,
    radius10: 10,
    radius12: 12,
    radius14: 14,
    radius16: 16,
    radius18: 18,
    radius20: 20,
    radius22: 22,
    radius24: 24,
    radius40: 40,
    radius36: 36,
    fullRound: 16777200,
    radius100:100
  },
  /**
   * Border width values for rounded corners
   * Usage: Typography.borderWidth.md
   */
  borderWidth: {
    borderWidth005: 0.5,
    borderWidth1: 1,
    borderWidth012: 1.2,
    borderWidth015: 1.5,
    borderWidth0125: 1.25,
    borderWidth2: 2,
    borderWidth4: 4,
    borderWidth6: 6,
    borderWidth8: 8,
    borderWidth10: 10,
    borderWidth12: 12,
    borderWidth14: 14,
    borderWidth16: 16,
    borderWidth18: 18,
    borderWidth5: 5,
  },
  /**
   * Standard icon sizes
   * Usage: Typography.icon.md
   */
  icon: {
    icon15: 15,
    icon16: 16,
    icon20: 20,
    icon24: 24,
    icon26: 26,
    icon28: 28,
    icon36: 36,
  },
  /**
   * Standard button heights
   * Usage: Typography.buttonHeight.md
   */
  buttonHeight: {
    buttonHeight36: 36,
    buttonHeight44: 44,
    buttonHeight52: 52,
  },
  /**
   * Standard line heights
   * Usage: Typography.lineHeight.md
   */
  lineHeight: {
    lineHeight2: 2,
    lineHeight4: 4,
    lineHeight6: 6,
    lineHeight8: 8,
    lineHeight10: 10,
    lineHeight12: 12,
    lineHeight14: 14,
    lineHeight16: 16,
    lineHeight18: 18,
    lineHeight20: 20,
    lineHeight22: 22,
    lineHeight24: 24,
    lineHeight26: 26,
    lineHeight28: 28,
    lineHeight30: 30,
    lineHeight32: 32,
    lineHeight34: 34,
    lineHeight36: 36,
    lineHeight38: 38,
    lineHeight40: 40,
    lineHeight42: 42,
    lineHeight44: 44,
  },
  /**
   * Standard margin
   * Usage: Typography.margin.md
   */
  margin: {
    margin2: 2,
    margin4: 4,
    margin5: 5,
    margin6: 6,
    margin8: 8,
    margin10: 10,
    margin12: 12,
    margin14: 14,
    margin16: 16,
    margin17: 17,
    margin18: 18,
    margin20: 20,
    margin22: 22,
    margin24: 24,
    margin26: 26,
    margin28: 28,
    margin30: 30,
    margin31: 31,
    margin32: 32,
    margin34: 34,
    margin36: 36,
    margin38: 38,
    margin40: 40,
    margin42: 42,
    margin44: 44,
    margin45: 45,
    margin46: 46,
    margin47: 47,
    margin55: 55,
    margin70: 70,
    margin80: 80,
    margin90: 90,
    margin100: 100,
    margin58: 58
  },
  /**
   * Standard paddings
   * Usage: Typography.padding.md
   */
  padding: {
    padding2: 2,
    padding4: 4,
    padding6: 6,
    padding8: 8,
    padding10: 10,
    padding11: 11,
    padding12: 12,
    padding14: 14,
    padding16: 16,
    padding18: 18,
    padding20: 20,
    padding21: 21,
    padding22: 22,
    padding24: 24,
    padding25: 25,
    padding26: 26,
    padding70: 70,
    padding30: 30,
    padding50: 50,
    padding19: 19,
    padding15: 15,
    padding41: 41,
    padding52: 52,
    padding60: 60,
    padding34: 34,
    padding35: 35,
    padding5: 5,
    padding17: 17,
    padding32: 32,
    padding33: 33,
    padding36: 36,
    padding7: 7,
    padding13: 13,
    padding72: 72,
    padding77: 77,
    padding9: 9,
    padding40: 40,
    padding49: 49,
    padding45: 45,
    padding159: 159,
    padding80: 80,
    padding180: 180,
    padding55: 55,
    padding28: 28
  },
  /**
   * Standard gaps
   * Usage: Typography.gap.md
   */
  gap: {
    gap2: 2,
    gap3: 3,
    gap4: 4,
    gap6: 6,
    gap8: 8,
    gap10: 10,
    gap12: 12,
    gap14: 14,
    gap16: 16,
    gap18: 18,
    gap19: 19,
    gap20: 20,
    gap22: 22,
    gap24: 24,
    gap32: 32,
    gap34: 34,
    gap35: 35,
    gap36: 36,
    gap26: 26,
    gap25: 25,
    gap5: 5,
    gap40: 40,
    gap50: 50,
    gap60: 60,
    gap27: 27,
    gap33: 33,
    gap73: 73,
    gap38: 38,
    gap126: 126,
    gap45: 45,
    gap48: 48,
    gap39: 39,
    gap110: 110,
    gap114: 114,
    gap80: 80,
    gap28: 28,
    gap64: 64,
    gap52: 52
  },
  /**
   * Input text pad
   * Usage: Typography.textInput.numberPad
   */
  textInput: {
    numberPad: 'number-pad',
    numeric: 'numeric',
    email: 'email-address',
  },
  /**
   * Font families
   * Usage: Typography.fontFamily.inter
   *
   */
  fontFamily: {
    inter: 'Inter-Regular',
    rethinkSans: 'RethinkSans-Regular',
  },
  /**
   * Common constants for layout dimensions
   */
  fullWidth: '100%',
  fullHeight: '100%',
  /**
   * Signature dimensions which is used for validation
   */
  signatureDimensions: {
    MIN_TOTAL_INK_LENGTH: 200,
    MIN_SIGNIFICANT_STROKES_REQUIRED: 1,
    MAX_ALLOWED_TINY_STROKES: 4,
    MIN_OVERALL_WIDTH: 80,
    MIN_OVERALL_HEIGHT: 30,
  },
  /**
   * Standard heights
   * Usage: Typography.height.md
   */
  height: {
    // Small Sizes
    height1: isMobile ? hp(0.12) : hp(0.1), // Result: 1
    height2: isMobile ? hp(0.25) : hp(0.19), // Result: 2
    height3: isMobile ? hp(0.37) : hp(0.29), // Result: 3
    height4: isMobile ? hp(0.5) : hp(0.39), // Result: 4
    height6: isMobile ? hp(0.75) : hp(0.58), // Result: 6
    height8: isMobile ? hp(1) : hp(0.77), // Result: 8
    height9: isMobile ? hp(1.125) : hp(0.875), // Result: 9
    height10: isMobile ? hp(1.25) : hp(0.97), // Result: 10

    // Medium Sizes
    height12: isMobile ? hp(1.5) : hp(1.16), // Result: 12
    height14: isMobile ? hp(1.75) : hp(1.35), // Result: 14
    height15: isMobile ? hp(1.875) : hp(1.46), // Result: 15
    height16: isMobile ? hp(2) : hp(1.55), // Result: 16
    height18: isMobile ? hp(2.25) : hp(1.74), // Result: 18
    height20: isMobile ? hp(2.5) : hp(1.93), // Result: 20
    height22: isMobile ? hp(2.75) : hp(2.13), // Result: 22
    height24: isMobile ? hp(3) : hp(2.32), // Result: 24

    // Sequential Spacing
    height26: isMobile ? hp(3.25) : hp(2.51), // Result: 26
    height27: isMobile ? hp(3.375) : hp(2.61), // Result: 27
    height28: isMobile ? hp(3.5) : hp(2.71), // Result: 28
    height30: isMobile ? hp(3.75) : hp(2.9), // Result: 30
    height32: isMobile ? hp(4) : hp(3.09), // Result: 32
    height34: isMobile ? hp(4.25) : hp(3.29), // Result: 34
    height35: isMobile ? hp(4.375) : hp(3.38), // Result: 35
    height36: isMobile ? hp(4.5) : hp(3.48), // Result: 36
    height38: isMobile ? hp(4.75) : hp(3.67), // Result: 38
    height40: isMobile ? hp(5) : hp(3.87), // Result: 40
    height42: isMobile ? hp(5.25) : hp(4.06), // Result: 42
    height44: isMobile ? hp(5.5) : hp(4.25), // Result: 44
    height45: isMobile ? hp(5.625) : hp(4.35), // Result: 45
    height48: isMobile ? hp(6) : hp(4.64), // Result: 48
    // dropDownheight: isMobile ? hp(6) : hp(5), // Result: 51
    dropDownheight: isMobile ? hp(4.5) : Platform?.OS === 'web' ? hp(4.25) : hp(4), // Result: 51

    // Large & Custom Sizes
    height50: isMobile ? hp(6.25) : hp(4.85), // Result: 50
    height56: isMobile ? hp(7) : hp(5.41), // Result: 56
    height70: isMobile ? hp(8.75) : hp(6.8), // Result: 70
    height60: isMobile ? hp(7.5) : hp(5.8), // Result: 60
    height64: isMobile ? hp(8) : hp(6.18), // Result: 64
    height72: isMobile ? hp(9) : hp(7), // Result: 72 (Verified)
    height85: isMobile ? hp(10.625) : hp(8.225), // Result: 85
    height86: isMobile ? hp(10.75) : hp(8.32), // Result: 86
    height87: isMobile ? hp(10.875) : hp(8.41), // Result: 87
    height88: isMobile ? hp(11) : hp(8.5), // Result: 88
    height96: isMobile ? hp(12) : hp(9.3), // Result: 96
    height90: isMobile ? hp(11.25) : hp(8.7), // Result: 90
    height100: isMobile ? hp(12.5) : hp(9.66), // Result: 100
    height120: isMobile ? hp(15) : hp(11.6), // Result: 120
    height200: isMobile ? hp(25) : hp(19.3), // Result: 200
    height150: isMobile ? hp(18.75) : hp(14.5), // Result: 150
    height160: isMobile ? hp(20) : hp(15.5), // Result: 160
    height379: isMobile ? hp(47.375) : hp(36.7), // Result: 379
    height360: isMobile ? hp(45) : hp(34.8), // Result: 360
    height80: isMobile ? hp(10) : hp(7.7), // Result: 80
    height74: isMobile ? hp(9.3) : hp(7.2), // Result: 74
    height79: isMobile ? hp(9.875) : hp(7.6), // Result: 79
    height500: isMobile ? hp(62.5) : hp(48.3), // Result: 500
    height5: isMobile ? hp(0.625) : hp(0.4), // Result: 5
    height219: isMobile ? hp(27.375) : hp(21.2), // Result: 219
    height220: isMobile ? hp(27.5) : hp(21.3), // Result: 220
    height228: isMobile ? hp(28.5) : hp(22), // Result: 228
    height800: isMobile ? hp(100) : hp(77.2), // Result: 800
    height810: isMobile ? hp(101.25) : hp(78.8), // Result: 810
    height850: isMobile ? hp(106.25) : hp(82.7), // Result: 850
    height650: isMobile ? hp(81.25) : hp(62.8), // Result: 650
    height700: isMobile ? hp(87.5) : hp(67.7), // Result: 700
    height710: isMobile ? hp(88.75) : hp(68.6), // Result: 710
    height240: isMobile ? hp(30) : hp(23.2), // Result: 240
    height250: isMobile ? hp(31.25) : hp(24.2), // Result: 250
    height260: isMobile ? hp(32.5) : hp(25.2), // Result: 260
    height300: isMobile ? hp(37.5) : hp(29), // Result: 300,
    height417: isMobile ? hp(52.25) : hp(40.45), // Result: 417,
    height430: isMobile ? hp(54) : hp(41.8), // Result: 430
    height447: isMobile ? hp(56.1) : hp(43.4), // result 447
    height320: isMobile ? hp(40) : hp(31), // Result: 320
    height390: isMobile ? hp(48.75) : hp(37.7), // Result: 390
    height310: isMobile ? hp(38.75) : hp(30), // Result: 310
    height630: isMobile ? hp(78.75) : hp(60.9), // Result: 630
    height640: isMobile ? hp(80) : hp(61.9), // Result: 640
    height644: isMobile ? hp(80.5) : hp(62.2), // Result: 644
    height550: isMobile ? hp(68.75) : hp(53.2), // Result: 550
    height554: isMobile ? hp(69.3) : hp(53.6), // Result: 554
    height600: isMobile ? hp(75) : hp(58.1), // Result: 600
    height544: isMobile ? hp(68) : hp(53.12), // Result: 544
    height312: isMobile ? hp(39) : hp(30.46), // Result: 312
    height384: isMobile ? hp(48) : hp(37.5), // Result: 384
    height750: isMobile ? hp(93.75) : hp(72.9), // Result: 750
    height900: isMobile ? hp(112.5) : hp(87.4), // Result: 900 (~90 % of the 1028 pt web base)
    height232: isMobile ? hp(29) : hp(22.4), // Result: 232
    height442: isMobile ? hp(55.25) : hp(42.8), // Result: 442
    height636: isMobile ? hp(79.5) : hp(61.4), // Result: 636
    height137: isMobile ? hp(17.125) : hp(13.2), // Result: 137
    height317: isMobile ? hp(39.625) : hp(30.66), // Result: 317
  },
  /**
   * Standard Widths
   * Usage: Typography.width.md
   */
  width: {
    // Basic Scale
    width2: isMobile ? wp(0.25) : wp(0.15), // Result: 2
    width3: isMobile ? wp(0.37) : wp(0.22), // Result: 3
    width4: isMobile ? wp(0.5) : wp(0.29), // Result: 4
    width6: isMobile ? wp(0.75) : wp(0.44), // Result: 6
    width8: isMobile ? wp(1) : wp(0.58), // Result: 8
    width10: isMobile ? wp(1.25) : wp(0.73), // Result: 10
    width12: isMobile ? wp(1.5) : wp(0.88), // Result: 12

    // Progressive Scale
    width14: isMobile ? wp(1.75) : wp(1.02), // Result: 14
    width16: isMobile ? wp(2) : wp(1.17), // Result: 16
    width18: isMobile ? wp(2.25) : wp(1.31), // Result: 18
    width20: isMobile ? wp(2.5) : wp(1.46), // Result: 20
    width22: isMobile ? wp(2.75) : wp(1.6), // Result: 22
    width24: isMobile ? wp(3) : wp(1.75), // Result: 24

    // Sequential Spacing
    width26: isMobile ? wp(3.25) : wp(1.9), // Result: 26
    width27: isMobile ? wp(3.375) : wp(1.97), // Result: 27
    width28: isMobile ? wp(3.5) : wp(2.04), // Result: 28
    width30: isMobile ? wp(3.75) : wp(2.19), // Result: 30
    width32: isMobile ? wp(4) : wp(2.33), // Result: 32
    width34: isMobile ? wp(4.25) : wp(2.48), // Result: 34
    width36: isMobile ? wp(4.5) : wp(2.63), // Result: 36
    width38: isMobile ? wp(4.75) : wp(2.77), // Result: 38
    width40: isMobile ? wp(5) : wp(2.92), // Result: 40
    width42: isMobile ? wp(5.25) : wp(3.06), // Result: 42
    width44: isMobile ? wp(5.5) : wp(3.21), // Result: 44
    width48: isMobile ? wp(6) : wp(3.5), // Result: 48

    // Large & Custom Sizes
    width50: isMobile ? wp(6.25) : wp(3.65), // Result: 50
    width56: isMobile ? wp(7) : wp(4.08), // Result: 56
    width60: isMobile ? wp(7.5) : wp(4.38), // Result: 60
    width64: isMobile ? wp(8) : wp(4.67), // Result: 64
    width72: isMobile ? wp(9) : wp(5.25), // Result: 72 (Verified Anchor)
    width76: isMobile ? wp(9.5) : wp(5.54), // Result: 76
    width90: isMobile ? wp(11.25) : wp(6.56), // Result: 90
    width96: isMobile ? wp(11.99) : wp(6.99), // Result96
    width106: isMobile ? wp(13.25) : wp(7.73), // Result: 106
    width300: isMobile ? wp(37.5) : wp(21.88), // Result: 300
    width307: isMobile ? wp(38.375) : wp(22.39), // Result: 307
    width319: isMobile ? wp(39.875) : wp(23.26), // Result: 319
    width294: isMobile ? wp(36.75) : wp(21.38), // Result: 294
    width288: isMobile ? wp(36) : wp(20.95), // Result: 288
    width290: isMobile ? wp(36.25) : wp(21.18), // Result: 290
    width219: isMobile ? wp(27.375) : wp(15.9), // Result: 219
    width205: isMobile ? wp(25.625) : wp(14.95), // Result: 205
    width210: isMobile ? wp(26.25) : wp(15.31), // Result: 210
    width200: isMobile ? wp(25) : wp(14.6), // Result: 200

    width360: isMobile ? wp(45) : wp(26.25), // 360
    width380: isMobile ? wp(47.5) : wp(27.71), // 380
    width400: isMobile ? wp(50) : wp(29.17), // 400
    width320: isMobile ? wp(40) : wp(23.33), // 320
    width273: isMobile ? wp(34.125) : wp(19.89), // 273
    width270: isMobile ? wp(33.75) : wp(19.68), // 270
    width252: isMobile ? wp(31.5) : wp(18.38), // 252
    width232: isMobile ? wp(29) : wp(16.92), // 232
    width220: isMobile ? wp(27.5) : wp(16.04), // 220
    width225: isMobile ? wp(28.13) : wp(16.41),
    width213: isMobile ? wp(26.625) : wp(15.52), // 213
    width215: isMobile ? wp(26.875) : wp(15.69), // Result: 215
    width198: isMobile ? wp(24.75) : wp(14.43), // 198
    width190: isMobile ? wp(23.75) : wp(13.84), // 190
    width184: isMobile ? wp(23) : wp(13.41), // 184
    width145: isMobile ? wp(18.125) : wp(10.57), // 145
    width140: isMobile ? wp(17.5) : wp(10.2), // 140
    width138: isMobile ? wp(17.25) : wp(10.06), // 138
    width135: isMobile ? wp(16.875) : wp(9.84), // 135
    width132: isMobile ? wp(16.5) : wp(9.62), // 132
    width130: isMobile ? wp(16.25) : wp(9.48), // 130
    width125: isMobile ? wp(15.625) : wp(9.12), // 125
    width120: isMobile ? wp(15) : wp(8.75), // 120
    width110: isMobile ? wp(13.75) : wp(8.02), // 110
    width100: isMobile ? wp(12.5) : wp(7.29), // 100
    width80: isMobile ? wp(10) : wp(5.83), // 80
    width79: isMobile ? wp(9.875) : wp(5.76), // 79
    width5: isMobile ? wp(0.625) : wp(0.36), // 5
    width41: isMobile ? wp(5.125) : wp(2.99), // Result: 41
    width15: isMobile ? wp(1.875) : wp(1.09), // Result: 15
    width566: isMobile ? wp(70.75) : wp(41.28), // Result: 566
    width448: isMobile ? wp(56) : wp(32.67), // Result: 448
    width490: isMobile ? wp(61.25) : wp(35.73), // Result: 490
    width160: isMobile ? wp(20) : wp(11.66),    // Result: 160
    width164: isMobile ? wp(20.5) : wp(11.93), // Result: 164
    width161: isMobile ? wp(20.125) : wp(11.74), // Result: 161 — Stepper uniform step width
    width165: isMobile ? wp(20.625) : wp(12.02), // Result: 165
    width192: isMobile ? wp(24) : wp(14.0), // Result: 192
    width545: isMobile ? wp(68.125) : wp(39.73), // Result: 545
    width549: isMobile ? wp(68.625) : wp(40.03), // Result: 549
    width552: isMobile ? wp(69) : wp(40.24), // Result: 552
    width807: isMobile ? wp(100.875) : wp(58.85), // Result: 807
    width667: isMobile ? wp(83.375) : wp(48.65), // Result: 667
    width570: isMobile ? wp(71.25) : wp(41.56), // Result: 570
    width575: isMobile ? wp(71.875) : wp(41.93), // Result: 575
    width580: isMobile ? wp(72.5) : wp(42.29), // Result: 580
    width500: isMobile ? wp(62.5) : wp(36.46), // Result: 500
    width520: isMobile ? wp(65) : wp(37.93), // Result: 520
    width512: isMobile ? wp(64) : wp(37.48), // Result: 512
    width510: isMobile ? wp(63.75) : wp(37.13), // Result: 510
    width700: isMobile ? wp(87.5) : wp(51.24), // Result: 700
    width1120: isMobile ? wp(140) : wp(82), // Result: 1120
    width1072: isMobile ? wp(134) : wp(78.5), // Result: 1072
    width1026: isMobile ? wp(128.25) : wp(75.1), // Result: 1026
    width187: isMobile ? wp(23.375) : wp(13.68), // Result: 187
    width153: isMobile ? wp(19.125) : wp(11.17), // Result: 153
    width896: isMobile ? wp(112) : wp(65.6), // Result: 896
    width222: isMobile ? wp(27.75) : wp(16.19), // Result: 222 — Step 1
    width144: isMobile ? wp(18.0)  : wp(10.5),  // Result: 144 — Step 2
    width122: isMobile ? wp(15.25) : wp(8.9),   // Result: 122 — Step 4
    width124: isMobile ? wp(15.5) : wp(9.09),   // Result: 124
    width115: isMobile ? wp(14.375): wp(8.39),  // Result: 115 — Step 5
    width201: isMobile ? wp(25.125) : wp(14.66), // Result: 201
    width127: isMobile ? wp(15.875) : wp(9.26),  // Result: 127
    width129: isMobile ? wp(16.125) : wp(9.41),  // Result: 129
    width111: isMobile ? wp(13.875) : wp(8.10),  // Result: 111
    width113: isMobile ? wp(14.125) : wp(8.25),  // Result: 113
    width105: isMobile ? wp(13.125) : wp(7.66),  // Result: 105
    width978: isMobile ? wp(122.25) : wp(71.6),  // Result: 978
    width229: isMobile ? wp(28.625) : wp(16.69), // Result: 229
    width154: isMobile ? wp(19.25) : wp(11.22),  // Result: 154
    width344: isMobile ? wp(43) : wp(25.07),      // Result: 344
    width412: isMobile ? wp(51.5) : wp(30), // Result: 412
    width116: isMobile ? wp(14.5) : wp(8.48), // Result: 116
    width176: isMobile ? wp(22) : wp(12.92), // Result: 176
    width224: isMobile ? wp(28) : wp(16.41), // Result: 224
    width256: isMobile ? wp(32) : wp(18.69), // Result: 256
    width260: isMobile ? wp(32.5) : wp(18.98), // Result: 260
    width342: isMobile ? wp(42.75) : wp(25.04), // Result: 342
    width676: isMobile ? wp(84.5) : wp(49.27), // Result: 676
  },
  /**
   * Standard text alignment
   * Usage: Typography.textAlign.leftAlign
   */
  textAlign: {
    leftAlign: 'left',
    rightAlign: 'right',
    centerAlign: 'center',
  },
  /**
   * Standard content alignment
   * Usage: Typography.contentAlign.spaceBetween
   */
  contentAlign: {
    spaceBetween: 'space-between',
    spaceAround: 'space-around',
    flexStart: 'flex-start',
    flexEnd: 'flex-end',
  },
  /**
   * Standard positions
   * Usage: Typography.position.absolute
   */
  position: {
    absolute: 'absolute',
    relative: 'relative',
  },
  /**
   * Text decorations
   * Usage: Typography.textDecoration.
   */
  textDecoration: {
    underline: 'underline',
  },

  /**
   *  Flexbox properties for layout consistency 
   *  Usage: Typography.flexDirection.row
   */

  flexDirection: {
    row: 'row',
    column: 'column',
    justifyContent: 'center',
  },
} as const;

export default Typography;
